import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X, Pencil, Loader2 } from 'lucide-react';
import { cn, HOVER_REVEAL } from '@/lib/utils';

interface InlineEditFieldProps {
  value: string;
  onSave: (value: string) => Promise<void> | void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  displayClassName?: string;
  type?: 'text' | 'email' | 'tel' | 'url';
  multiline?: boolean;
  /** How to trigger edit mode: 'button' shows pencil icon, 'click' makes text directly clickable */
  editTrigger?: 'button' | 'click';
}

export function InlineEditField({
  value,
  onSave,
  placeholder = 'No value set...',
  className,
  inputClassName,
  displayClassName,
  type = 'text',
  multiline = false,
  editTrigger = 'button',
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (currentValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(currentValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      setCurrentValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCurrentValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    if (editTrigger === 'click') {
      handleSave();
    }
  };

  if (isEditing) {
    if (multiline) {
      return (
        <div className={cn('space-y-2', className)}>
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            placeholder={placeholder}
            className={cn(
              // text-base below md keeps iOS Safari from zooming the viewport
              // when this gets focus — same rule as the shared Input/Textarea.
              'w-full px-3 py-2 text-base md:text-sm border border-input rounded-md',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'resize-none min-h-[60px]',
              inputClassName
            )}
            rows={3}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="h-8"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="h-8"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    // Click trigger mode: just the input, blur saves, Escape cancels
    if (editTrigger === 'click') {
      return (
        <div className={cn('relative', className)}>
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={isSaving}
            placeholder={placeholder}
            className={cn(isSaving && 'pr-8', inputClassName)}
          />
          {isSaving && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      );
    }

    // Button trigger mode: input with save/cancel buttons
    return (
      <div className={cn('flex gap-2 items-center', className)}>
        <Input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={type}
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          placeholder={placeholder}
          className={cn('flex-1', inputClassName)}
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="h-9 px-3"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCancel}
          disabled={isSaving}
          className="h-9 px-3"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      onClick={editTrigger === 'click' ? () => setIsEditing(true) : undefined}
      className={cn(
        'px-3 py-2 text-sm rounded-md',
        'min-h-[2.5rem] flex items-center gap-2',
        editTrigger === 'button' && 'justify-between group',
        editTrigger === 'click' && 'cursor-pointer hover:bg-muted/50 transition-colors',
        displayClassName,
        className
      )}
    >
      <span className={cn('break-words min-w-0', !value && 'text-muted-foreground italic')}>
        {value || placeholder}
      </span>
      {editTrigger === 'button' && (
        <Button
          size="sm"
          variant="ghost"
          aria-label="Edit"
          onClick={() => setIsEditing(true)}
          className={cn('h-9 shrink-0 px-3 sm:h-7 sm:px-2', HOVER_REVEAL)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
